import React from "react";
import { motion } from "framer-motion";
import { Play, ExternalLink } from "lucide-react";
import { TRACKS } from "@/lib/data";

export const Music = () => {
  // The embedded YouTube card stays on Money Tonight (the embeddable cut).
  // Clicking a track in the list opens it directly on YouTube Music in a new tab,
  // since most YouTube Music "Topic" uploads disallow third-party embedding.
  const ytCard = { id: "lwWAeg4D7pQ", title: "Money Tonight" };

  return (
    <section id="music" data-testid="music-section" className="relative py-28 md:py-40 bg-ink-900/40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="text-xs tracking-[0.4em] uppercase text-cyan-glow mb-3">
              ◢ Stream the Tsunami
            </div>
            <h2 className="font-display font-black uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              The <span className="text-shimmer-gold">Discography</span>
              <br />
              <span className="text-outline">2023 · 2026</span>
            </h2>
          </div>
          <div className="font-serif italic text-slate-400 text-lg max-w-xs">
            Ten singles. One tsunami. Every drop hits like landfall.
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 lg:gap-12">
          {/* Tracklist */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="col-span-12 lg:col-span-7"
            data-testid="tracklist"
          >
            <div className="border-t border-white/10">
              {TRACKS.map((t) => {
                const playable = !!t.yt;
                const href = playable
                  ? `https://music.youtube.com/watch?v=${t.yt}`
                  : undefined;
                return (
                  <a
                    key={t.n}
                    data-testid={`track-${t.n}`}
                    href={href}
                    target={playable ? "_blank" : undefined}
                    rel={playable ? "noopener noreferrer" : undefined}
                    aria-disabled={!playable}
                    className={`group w-full text-left flex items-center gap-5 py-5 md:py-6 border-b border-white/10 transition-all ${
                      playable
                        ? "hover:bg-gradient-to-r hover:from-gold/10 hover:via-transparent hover:to-transparent cursor-pointer"
                        : "opacity-60 cursor-not-allowed pointer-events-none"
                    }`}
                  >
                    <span className="font-mono text-xs w-10 text-slate-500 group-hover:text-gold transition-colors">
                      {t.n}
                    </span>
                    <span
                      className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all ${
                        playable
                          ? "border-white/20 text-slate-300 group-hover:bg-gold group-hover:border-gold group-hover:text-ink-950"
                          : "border-white/10 text-slate-600"
                      }`}
                    >
                      <Play size={12} fill="currentColor" />
                    </span>
                    <span className="flex-1 flex items-baseline gap-3 flex-wrap">
                      <span className="font-display font-bold text-lg md:text-2xl tracking-tight text-slate-200 group-hover:text-white">
                        {t.title}
                      </span>
                      {t.featured && (
                        <span className="text-[9px] tracking-[0.3em] uppercase text-gold border border-gold/40 px-2 py-0.5">
                          Single
                        </span>
                      )}
                      {!playable && (
                        <span className="text-[9px] tracking-[0.3em] uppercase text-slate-500">
                          Audio Only
                        </span>
                      )}
                    </span>
                    <span className="font-mono text-xs text-slate-500">{t.year}</span>
                  </a>
                );
              })}
            </div>
          </motion.div>

          {/* Embeds */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="col-span-12 lg:col-span-5 space-y-6"
          >
            <div className="bg-ink-950/60 backdrop-blur-md border border-white/10 p-6 hover:border-gold/40 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] tracking-[0.4em] uppercase text-gold">Apple Music · Now Playing</span>
                <a
                  href="https://music.apple.com/us/album/latuya-bay/6771038417?i=6771038418"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="apple-music-link"
                  className="text-slate-400 hover:text-gold"
                >
                  <ExternalLink size={14} />
                </a>
              </div>
              <iframe
                title="Latuya Bay — Cassius Grate"
                data-testid="apple-music-embed-latuya-bay"
                allow="autoplay *; encrypted-media *; clipboard-write"
                frameBorder="0"
                height="175"
                style={{
                  width: "100%",
                  maxWidth: "660px",
                  overflow: "hidden",
                  background: "transparent",
                  borderRadius: "10px",
                }}
                sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                src="https://embed.music.apple.com/us/album/latuya-bay/6771038417?i=6771038418"
              ></iframe>
              <p className="mt-4 text-xs text-slate-400 font-light">
                <span className="text-gold font-medium">LATUYA BAY</span> — the new single.
                Tap play. Drown willingly.
              </p>
            </div>

            <div className="bg-ink-950/60 backdrop-blur-md border border-white/10 p-6 hover:border-cyan-neon/40 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] tracking-[0.4em] uppercase text-cyan-glow">
                  YouTube · {ytCard.title}
                </span>
                <a
                  href={`https://music.youtube.com/watch?v=${ytCard.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="youtube-link"
                  className="text-slate-400 hover:text-cyan-glow"
                >
                  <ExternalLink size={14} />
                </a>
              </div>
              <div className="aspect-video border border-white/5 overflow-hidden">
                <iframe
                  title={`${ytCard.title} — Cassius Grate (YouTube)`}
                  data-testid="youtube-embed"
                  src={`https://www.youtube.com/embed/${ytCard.id}?rel=0&modestbranding=1`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              <p className="mt-4 text-xs text-slate-400 font-light">
                Tap any track above to open it on YouTube Music.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
